import { Link } from "react-router-dom";
import { 
  Briefcase, 
  Users, 
  FileText,
  ArrowRight,
  CheckCircle2,
  Clock,
  BarChart3,
  MessageSquare,
  Target,
  Zap,
  Shield,
  ClipboardCheck,
  Calendar,
  TrendingUp,
  Eye,
  Sparkles,
  Code,
  Search,
  UserCheck,
  LineChart,
  Lock,
  Download,
  Mail,
  ChevronRight,
  Star,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

export default function Landing() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="bg-card/80 backdrop-blur-lg border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-heading font-bold text-xl text-foreground">
                TalentMatcher
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/billing">
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                  Planes
                </Button>
              </Link>
              <Link to="/login">
                <Button size="sm" className="rounded-full">
                  Iniciar Sesión
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-16 md:py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none" />
          <div className="max-w-4xl mx-auto text-center relative">
            <Badge className="mb-6 bg-primary/10 text-primary border-primary/20 hover:bg-primary/15">
              Nuevo: Evaluación Post-Entrevista con IA
            </Badge>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
              Del CV a la contratación,
              <br />
              <span className="text-primary">todo en un solo lugar.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Analiza candidatos con IA, valida en entrevistas y genera planes de desarrollo. 
              Decisiones de contratación más inteligentes, más rápidas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="rounded-full gap-2 w-full sm:w-auto">
                  Empezar gratis
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full gap-2 w-full sm:w-auto">
                  Ya tengo cuenta
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Process Flow - New Section */}
        <section className="py-12 px-4 bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-center mb-4">
              Tu proceso de contratación, simplificado
            </h2>
            <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
              De un montón de CVs a una decisión clara en 4 pasos
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Step 1 */}
              <div className="relative">
                <div className="bg-card border rounded-xl p-6 h-full">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mb-4">
                    1
                  </div>
                  <FileText className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Sube los CVs</h3>
                  <p className="text-sm text-muted-foreground">
                    Hasta 10 CVs a la vez. Nuestra IA extrae toda la información relevante.
                  </p>
                </div>
                <ChevronRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 text-muted-foreground/30 z-10" />
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="bg-card border rounded-xl p-6 h-full">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mb-4">
                    2
                  </div>
                  <Target className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Ranking Automático</h3>
                  <p className="text-sm text-muted-foreground">
                    Cada candidato recibe un % de match. Tú defines los pesos de cada criterio.
                  </p>
                </div>
                <ChevronRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 text-muted-foreground/30 z-10" />
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="bg-card border rounded-xl p-6 h-full">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mb-4">
                    3
                  </div>
                  <ClipboardCheck className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Evalúa Entrevistas</h3>
                  <p className="text-sm text-muted-foreground">
                    Valida si el CV coincide con la persona real. Identifica brechas.
                  </p>
                </div>
                <ChevronRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 text-muted-foreground/30 z-10" />
              </div>

              {/* Step 4 */}
              <div>
                <div className="bg-card border rounded-xl p-6 h-full border-primary/30">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mb-4">
                    4
                  </div>
                  <Calendar className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Plan de Desarrollo</h3>
                  <p className="text-sm text-muted-foreground">
                    Genera automáticamente un plan 30-60-90 días para cerrar brechas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* User Path Selection - Enhanced */}
        <section className="py-16 md:py-20 px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-center mb-4">
              ¿Qué necesitas hoy?
            </h2>
            <p className="text-center text-muted-foreground mb-10">
              Elige tu camino y empieza en segundos
            </p>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              
              {/* Recruiter Card - Enhanced */}
              <Link to="/register" className="group">
                <Card className="h-full border-2 hover:border-primary/50 hover:shadow-xl transition-all duration-300">
                  <CardContent className="pt-8 pb-6 px-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                      <Users className="w-7 h-7 text-primary" />
                    </div>
                    
                    <h2 className="font-heading text-2xl font-bold mb-3 text-foreground">
                      Soy Reclutador
                    </h2>
                    <p className="text-muted-foreground mb-6 text-sm md:text-base">
                      Contrata mejor y más rápido. Desde el CV hasta el plan de onboarding.
                    </p>

                    <div className="space-y-4 mb-8">
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-primary" />
                          Análisis de Candidatos
                        </h4>
                        <ul className="space-y-1 text-xs text-muted-foreground">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Ranking por % de match personalizable
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Comparador lado a lado de candidatos
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Smart Shortlist generada por IA
                          </li>
                        </ul>
                      </div>

                      <div className="p-3 bg-muted/50 rounded-lg">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <ClipboardCheck className="w-4 h-4 text-primary" />
                          Evaluación Post-Entrevista
                        </h4>
                        <ul className="space-y-1 text-xs text-muted-foreground">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Validación CV vs Persona real
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Índice de alineación automático
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Plan 30-60-90 días con IA
                          </li>
                        </ul>
                      </div>

                      <div className="p-3 bg-muted/50 rounded-lg">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <Code className="w-4 h-4 text-primary" />
                          Integraciones (Plan Business)
                        </h4>
                        <ul className="space-y-1 text-xs text-muted-foreground">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            API para conectar tu ATS
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Envío automático de reportes por email
                          </li>
                        </ul>
                      </div>
                    </div>

                    <Button className="w-full gap-2 group-hover:gap-3 transition-all rounded-full">
                      Empezar como reclutador
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              </Link>

              {/* Candidate Card - Enhanced */}
              <Link to="/register" className="group">
                <Card className="h-full border-2 hover:border-green-500/50 hover:shadow-xl transition-all duration-300">
                  <CardContent className="pt-8 pb-6 px-6">
                    <div className="w-14 h-14 rounded-2xl bg-green-500/10 flex items-center justify-center mb-6 group-hover:bg-green-500/20 transition-colors">
                      <FileText className="w-7 h-7 text-green-600" />
                    </div>
                    
                    <h2 className="font-heading text-2xl font-bold mb-3 text-foreground">
                      Busco Empleo
                    </h2>
                    <p className="text-muted-foreground mb-6 text-sm md:text-base">
                      Destaca entre otros candidatos. Optimiza tu CV y deja que las empresas te encuentren.
                    </p>

                    <div className="space-y-4 mb-8">
                      <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-green-600" />
                          Optimización de CV con IA
                        </h4>
                        <ul className="space-y-1 text-xs text-muted-foreground">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Reescritura específica para cada puesto
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Mejora tu score de match
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Sugerencias de habilidades a destacar
                          </li>
                        </ul>
                      </div>

                      <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <Search className="w-4 h-4 text-green-600" />
                          Análisis de Compatibilidad ATS
                        </h4>
                        <ul className="space-y-1 text-xs text-muted-foreground">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Verifica si tu CV pasa filtros automáticos
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Detecta problemas de formato
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Sugerencias de mejora específicas
                          </li>
                        </ul>
                      </div>

                      <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <UserCheck className="w-4 h-4 text-green-600" />
                          Matching Bidireccional
                        </h4>
                        <ul className="space-y-1 text-xs text-muted-foreground">
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Las empresas te encuentran a ti
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Notificaciones de oportunidades
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            Control total de tu visibilidad
                          </li>
                        </ul>
                      </div>
                    </div>

                    <Button variant="outline" className="w-full gap-2 group-hover:gap-3 transition-all rounded-full border-green-500 text-green-600 hover:bg-green-50">
                      Optimizar mi CV ahora
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </section>

        {/* Feature Highlights */}
        <section className="py-16 px-4 bg-gradient-to-b from-muted/50 to-background">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-center mb-4">
              Funcionalidades que marcan la diferencia
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              No somos otro analizador de CVs. Somos tu copiloto en todo el proceso de contratación.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Feature 1 */}
              <Card className="border-0 shadow-lg">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-950 flex items-center justify-center mb-4">
                    <ClipboardCheck className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Evaluación Post-Entrevista</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    ¿El CV decía una cosa y en la entrevista viste otra? Captura esas diferencias 
                    y ajusta automáticamente el score del candidato.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex items-center gap-2 text-muted-foreground">
                      <Star className="w-3 h-3 text-amber-500" />
                      Índice de alineación CV-Persona
                    </li>
                    <li className="flex items-center gap-2 text-muted-foreground">
                      <Star className="w-3 h-3 text-amber-500" />
                      Identificación de riesgos
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Feature 2 */}
              <Card className="border-0 shadow-lg">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-950 flex items-center justify-center mb-4">
                    <Calendar className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Plan 30-60-90 Días</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    ¿Contratas con brechas? Generamos automáticamente un plan de desarrollo 
                    personalizado para cerrarlas.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex items-center gap-2 text-muted-foreground">
                      <Star className="w-3 h-3 text-amber-500" />
                      Generado con IA en segundos
                    </li>
                    <li className="flex items-center gap-2 text-muted-foreground">
                      <Star className="w-3 h-3 text-amber-500" />
                      Descargable y enviable por email
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Feature 3 */}
              <Card className="border-0 shadow-lg">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-950 flex items-center justify-center mb-4">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Matching Bidireccional</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Los candidatos también pueden ser encontrados por reclutadores. 
                    Conexiones que benefician a ambos.
                  </p>
                  <ul className="text-xs space-y-1">
                    <li className="flex items-center gap-2 text-muted-foreground">
                      <Star className="w-3 h-3 text-amber-500" />
                      Solo con consentimiento activo
                    </li>
                    <li className="flex items-center gap-2 text-muted-foreground">
                      <Star className="w-3 h-3 text-amber-500" />
                      Control total de privacidad
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Trust & Security Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                Tus datos, protegidos
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Cumplimos con estándares internacionales de protección de datos. 
                Los candidatos tienen control total sobre su información.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-4">
                <Lock className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-medium mb-1">Consentimiento explícito</h3>
                <p className="text-xs text-muted-foreground">
                  Nada sin permiso del candidato
                </p>
              </div>
              <div className="text-center p-4">
                <Clock className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-medium mb-1">Retención limitada</h3>
                <p className="text-xs text-muted-foreground">
                  Datos eliminados tras 12 meses de inactividad
                </p>
              </div>
              <div className="text-center p-4">
                <Eye className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-medium mb-1">Transparencia total</h3>
                <p className="text-xs text-muted-foreground">
                  Los candidatos ven quién accedió a su perfil
                </p>
              </div>
              <div className="text-center p-4">
                <Download className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-medium mb-1">Portabilidad</h3>
                <p className="text-xs text-muted-foreground">
                  Descarga o elimina tus datos cuando quieras
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Value Props */}
        <section className="py-12 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-medium text-foreground mb-1">Rápido</h3>
                <p className="text-sm text-muted-foreground">
                  De horas a minutos revisando CVs
                </p>
              </div>

              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <BarChart3 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-medium text-foreground mb-1">Objetivo</h3>
                <p className="text-sm text-muted-foreground">
                  Mismos criterios para todos
                </p>
              </div>

              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-medium text-foreground mb-1">Claro</h3>
                <p className="text-sm text-muted-foreground">
                  Sabes qué cumple y qué falta
                </p>
              </div>

              <div className="text-center p-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <MessageSquare className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-medium text-foreground mb-1">Justificable</h3>
                <p className="text-sm text-muted-foreground">
                  Explica decisiones con datos
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Plans Preview */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
              Un plan para cada equipo
            </h2>
            <p className="text-muted-foreground mb-8">
              Desde freelancers hasta equipos de RRHH. Empieza gratis.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="border-2">
                <CardContent className="pt-6">
                  <h3 className="font-bold text-lg">Free</h3>
                  <p className="text-3xl font-bold my-2">$0</p>
                  <p className="text-sm text-muted-foreground mb-4">Para probar</p>
                  <ul className="text-sm text-left space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      1 puesto activo
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      10 CVs/mes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Análisis básico
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-primary relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary">Popular</Badge>
                </div>
                <CardContent className="pt-6">
                  <h3 className="font-bold text-lg">Pro</h3>
                  <p className="text-3xl font-bold my-2">$20<span className="text-sm font-normal">/mes</span></p>
                  <p className="text-sm text-muted-foreground mb-4">Para reclutadores activos</p>
                  <ul className="text-sm text-left space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      5 puestos activos
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      100 CVs/mes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Evaluación post-entrevista
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Plan 30-60-90 días
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="pt-6">
                  <h3 className="font-bold text-lg">Business</h3>
                  <p className="text-3xl font-bold my-2">$50<span className="text-sm font-normal">/mes</span></p>
                  <p className="text-sm text-muted-foreground mb-4">Para equipos de RRHH</p>
                  <ul className="text-sm text-left space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Puestos ilimitados
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      CVs ilimitados
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      API Access
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Soporte prioritario
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Link to="/billing">
              <Button variant="outline" className="rounded-full">
                Ver todos los detalles de planes
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-20 px-4 bg-primary/5">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
              ¿Listo para contratar mejor?
            </h2>
            <p className="text-muted-foreground mb-8">
              Empieza gratis hoy. Sin tarjeta de crédito. Cancela cuando quieras.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="rounded-full gap-2 w-full sm:w-auto">
                  <Sparkles className="w-4 h-4" />
                  Crear cuenta gratis
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full gap-2 w-full sm:w-auto">
                  Ya tengo cuenta
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 bg-card">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Briefcase className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-heading font-semibold text-foreground">TalentMatcher</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/billing" className="hover:text-foreground transition-colors">
                Planes
              </Link>
              <Link to="/docs/POLITICA_PRIVACIDAD.md" target="_blank" className="hover:text-foreground transition-colors">
                Privacidad
              </Link>
              <span>© 2026 TalentMatcher</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
